import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Plus, Edit, RefreshCw, Cog, AlertTriangle, Trash2 } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { Dialog } from '@/components/ui/Dialog';
import {
  useOrdresImpression, useCreateOrdreImpression, useUpdateOrdreImpression, useDeleteOrdreImpression,
  useMachines, useCommandesImpression,
} from '@/hooks/useTextprint';
import { useEmployees } from '@/hooks/useHr';
import {
  PRIORITE_OF_LABELS, STATUT_OF_IMPRESSION_LABELS,
  type OrdreImpression, type PrioriteOF, type StatutOFImpression,
} from '@/types';
import { extractError } from '@/api/client';
import { useAuthStore } from '@/stores/authStore';
import { demander } from '@/components/ui/Fenetres';

const STATUT_BADGE: Record<StatutOFImpression, string> = {
  en_attente:       'ds-bd ds-bd-n',
  en_cours:         'ds-bd ds-bd-i',
  controle_qualite: 'ds-bd ds-bd-w',
  termine:          'ds-bd ds-bd-s',
  annule:           'ds-bd ds-bd-d',
};

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : '—';

export const OrdresImpressionPage = () => {
  const filiale = useAuthStore((s) => s.filiale);
  const [statut, setStatut]     = useState<StatutOFImpression | ''>('');
  const [priorite, setPriorite] = useState<PrioriteOF | ''>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing]   = useState<OrdreImpression | undefined>();
  const [error, setError]       = useState<string | null>(null);

  const { data, isLoading, refetch } = useOrdresImpression({ statut: statut || undefined, priorite: priorite || undefined, limit: 200 });
  const ordres = data?.data ?? [];
  const remove = useDeleteOrdreImpression();

  const openCreate = () => { setEditing(undefined); setDialogOpen(true); };
  const openEdit   = (o: OrdreImpression) => { setEditing(o); setDialogOpen(true); };

  const onRemove = async (id: string, ref: string) => {
    if (!(await demander.confirmer({ titre: `Supprimer l'OF "${ref}" ?`, confirmer: 'Supprimer', danger: true }))) return;
    setError(null);
    try { await remove.mutateAsync(id); }
    catch (err) { setError(extractError(err).message); }
  };

  const count = (s: StatutOFImpression) => ordres.filter((o) => o.statut === s).length;

  return (
    <>
      <Topbar
        title="Ordres de fabrication"
        subtitle={filiale ? `${ordres.length} OF · ${filiale.fullName}` : 'OF Textprint'}
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()}><RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} /></button>
            <button className="ds-btn ds-btn-p" onClick={openCreate}><Plus size={14} /> Nouvel OF</button>
          </>
        }
      />
      <div className="ds-content">
        {error && <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}><span className="ds-alert-ico"><AlertTriangle size={18} /></span><div className="ds-alert-txt">{error}</div></div>}

        <div className="ds-kpi-row" style={{ marginBottom: 16 }}>
          <div className="ds-kr kr-c1"><div className="ds-kr-ico">⚙️</div><div className="ds-kr-val">{ordres.length}</div><div className="ds-kr-lbl">Total OF</div></div>
          <div className="ds-kr kr-c3"><div className="ds-kr-ico">⏳</div><div className="ds-kr-val">{count('en_attente')}</div><div className="ds-kr-lbl">En attente</div></div>
          <div className="ds-kr kr-c2"><div className="ds-kr-ico">🚀</div><div className="ds-kr-val">{count('en_cours')}</div><div className="ds-kr-lbl">En cours</div></div>
          <div className="ds-kr kr-c4"><div className="ds-kr-ico">✅</div><div className="ds-kr-val">{count('termine')}</div><div className="ds-kr-lbl">Terminés</div></div>
        </div>

        <div className="ds-filters">
          <select className="ds-fi" value={statut} onChange={(e) => setStatut(e.target.value as StatutOFImpression | '')} style={{ width: 'auto' }}>
            <option value="">Tous statuts</option>
            {(Object.keys(STATUT_OF_IMPRESSION_LABELS) as StatutOFImpression[]).map((s) => <option key={s} value={s}>{STATUT_OF_IMPRESSION_LABELS[s]}</option>)}
          </select>
          <select className="ds-fi" value={priorite} onChange={(e) => setPriorite(e.target.value as PrioriteOF | '')} style={{ width: 'auto' }}>
            <option value="">Toutes priorités</option>
            {(Object.keys(PRIORITE_OF_LABELS) as PrioriteOF[]).map((p) => <option key={p} value={p}>{PRIORITE_OF_LABELS[p]}</option>)}
          </select>
        </div>

        <div className="ds-table-panel">
          <div className="ds-table-header"><div><div className="ds-table-title">Ordres de fabrication</div><div className="ds-table-sub">{ordres.length} OF</div></div></div>
          <table className="ds-tbl">
            <thead><tr>
              <th style={{ width: 140 }}>Réf</th>
              <th>Désignation</th>
              <th>Machine</th>
              <th>Opérateur</th>
              <th className="ds-am" style={{ width: 70 }}>Qté</th>
              <th style={{ width: 90 }}>Cible</th>
              <th style={{ width: 80 }}>Priorité</th>
              <th style={{ width: 110 }}>Statut</th>
              <th style={{ width: 80 }}></th>
            </tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={9} style={{ textAlign: 'center', padding: 32, color: 'var(--tx-3)' }}>Chargement…</td></tr>}
              {!isLoading && ordres.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 48 }}>
                  <Cog size={28} style={{ margin: '0 auto 8px', color: 'var(--tx-3)' }} />
                  <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>Aucun OF</p>
                  <button className="ds-btn ds-btn-p ds-btn-sm" style={{ marginTop: 12 }} onClick={openCreate}><Plus size={12} /> Créer le premier OF</button>
                </td></tr>
              )}
              {!isLoading && ordres.map((o) => (
                <tr key={o.id}>
                  <td><span className="ds-ref-code">{o.reference}</span></td>
                  <td><div className="ds-entry-label">{o.designation}</div>{o.commande && <div className="ds-entry-sub">→ {o.commande.reference}</div>}</td>
                  <td style={{ fontSize: 12 }}>{o.machine?.nom ?? '—'}</td>
                  <td style={{ fontSize: 12 }}>{o.operateur?.fullName ?? '—'}</td>
                  <td className="ds-am">{o.quantite}</td>
                  <td style={{ fontSize: 11 }}>{fmtDate(o.dateCibleFin)}</td>
                  <td style={{ fontSize: 11, fontWeight: o.priorite === 'express' ? 700 : 400, color: o.priorite === 'express' ? 'var(--danger)' : o.priorite === 'urgente' ? 'var(--warning)' : 'var(--tx-2)' }}>
                    {PRIORITE_OF_LABELS[o.priorite]}
                  </td>
                  <td><span className={STATUT_BADGE[o.statut]}>{STATUT_OF_IMPRESSION_LABELS[o.statut]}</span></td>
                  <td><div style={{ display: 'flex', gap: 4 }}>
                    <button onClick={() => openEdit(o)} className="ds-row-btn" title="Modifier"><Edit size={13} /></button>
                    <button onClick={() => onRemove(o.id, o.reference)} className="ds-row-btn" title="Supprimer" disabled={remove.isPending}><Trash2 size={13} /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <OrdreFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} ordre={editing} />
    </>
  );
};

const OrdreFormDialog = ({ open, onClose, ordre }: { open: boolean; onClose: () => void; ordre?: OrdreImpression }) => {
  const isEdit = !!ordre;
  const [reference, setReference] = useState('');
  const [commandeId, setCommandeId] = useState('');
  const [machineId, setMachineId] = useState('');
  const [operateurId, setOperateurId] = useState('');
  const [designation, setDesignation] = useState('');
  const [quantite, setQuantite]   = useState('');
  const [dateCibleFin, setDateCibleFin] = useState('');
  const [priorite, setPriorite]   = useState<PrioriteOF>('normale');
  const [statut, setStatut]       = useState<StatutOFImpression>('en_attente');
  const [tempsEstime, setTempsEstime] = useState('');
  const [soustraitant, setSoustraitant] = useState('');
  const [soustraitantCout, setSoustraitantCout] = useState('');
  const [notes, setNotes]         = useState('');
  const [error, setError]         = useState<string | null>(null);

  const { data: machinesData }  = useMachines({ limit: 200 });
  const { data: commandesData } = useCommandesImpression({ limit: 200 });
  const { data: employeesData } = useEmployees({ limit: 200 });
  const machines  = machinesData?.data ?? [];
  const commandes = commandesData?.data ?? [];
  const employees = employeesData?.data ?? [];
  const create = useCreateOrdreImpression();
  const update = useUpdateOrdreImpression();

  useEffect(() => {
    if (!open) return;
    if (ordre) {
      setReference(ordre.reference);
      setCommandeId(ordre.commandeId ?? ''); setMachineId(ordre.machineId ?? '');
      setOperateurId(ordre.operateurId ?? '');
      setDesignation(ordre.designation); setQuantite(ordre.quantite.toString());
      setDateCibleFin(ordre.dateCibleFin?.slice(0, 10) ?? '');
      setPriorite(ordre.priorite); setStatut(ordre.statut);
      setTempsEstime(ordre.tempsEstimeMin?.toString() ?? '');
      setSoustraitant(ordre.soustraitant ?? '');
      setSoustraitantCout(ordre.soustraitantCoutCents ? String(Number(BigInt(ordre.soustraitantCoutCents)) / 100) : '');
      setNotes(ordre.notes ?? '');
    } else {
      setReference(''); setCommandeId(''); setMachineId(''); setOperateurId('');
      setDesignation(''); setQuantite(''); setDateCibleFin('');
      setPriorite('normale'); setStatut('en_attente');
      setTempsEstime(''); setSoustraitant(''); setSoustraitantCout(''); setNotes('');
    }
    setError(null);
  }, [open, ordre]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const payload = {
        reference: reference.trim() || undefined,
        commandeId: commandeId || undefined,
        machineId: machineId || undefined,
        operateurId: operateurId || undefined,
        designation: designation.trim(),
        quantite: parseInt(quantite, 10),
        dateCibleFin: dateCibleFin || undefined,
        priorite, statut,
        tempsEstimeMin: tempsEstime ? parseInt(tempsEstime, 10) : undefined,
        soustraitant: soustraitant.trim() || undefined,
        soustraitantCoutCents: soustraitantCout ? Math.round(parseFloat(soustraitantCout) * 100) : undefined,
        notes: notes.trim() || undefined,
      };
      if (isEdit && ordre) await update.mutateAsync({ id: ordre.id, payload });
      else await create.mutateAsync(payload);
      onClose();
    } catch (err) { setError(extractError(err).message); }
  };

  const canSubmit = designation.trim() && quantite && parseInt(quantite, 10) > 0;

  return (
    <Dialog open={open} onClose={onClose} title={isEdit ? `Modifier — ${ordre?.reference}` : 'Nouvel OF Textprint'} subtitle="Réf TX-OF-AAAA-NNNN auto si vide" size="lg">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"><AlertTriangle className="inline size-3 mr-1" />{error}</div>}

        <div className="grid grid-cols-3 gap-3">
          <Field label="Référence"><input value={reference} onChange={(e) => setReference(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="auto si vide" /></Field>
          <Field label="Priorité">
            <select value={priorite} onChange={(e) => setPriorite(e.target.value as PrioriteOF)} className="ds-fi" style={{ width: '100%' }}>
              {(Object.keys(PRIORITE_OF_LABELS) as PrioriteOF[]).map((p) => <option key={p} value={p}>{PRIORITE_OF_LABELS[p]}</option>)}
            </select>
          </Field>
          <Field label="Statut">
            <select value={statut} onChange={(e) => setStatut(e.target.value as StatutOFImpression)} className="ds-fi" style={{ width: '100%' }}>
              {(Object.keys(STATUT_OF_IMPRESSION_LABELS) as StatutOFImpression[]).map((s) => <option key={s} value={s}>{STATUT_OF_IMPRESSION_LABELS[s]}</option>)}
            </select>
          </Field>
        </div>

        <Field label="Désignation *">
          <input value={designation} onChange={(e) => setDesignation(e.target.value)} className="ds-fi" required style={{ width: '100%' }} placeholder="500 flyers A5 quadri R/V" />
        </Field>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Commande client">
            <select value={commandeId} onChange={(e) => setCommandeId(e.target.value)} className="ds-fi" style={{ width: '100%' }}>
              <option value="">— OF interne (sans commande) —</option>
              {commandes.map((c) => <option key={c.id} value={c.id}>{c.reference}{c.client ? ` · ${c.client.companyName ?? c.client.fullName}` : ''}</option>)}
            </select>
          </Field>
          <Field label="Machine assignée">
            <select value={machineId} onChange={(e) => setMachineId(e.target.value)} className="ds-fi" style={{ width: '100%' }}>
              <option value="">— Non assignée —</option>
              {machines.map((m) => <option key={m.id} value={m.id}>{m.code} — {m.nom}</option>)}
            </select>
          </Field>
          <Field label="Opérateur">
            <select value={operateurId} onChange={(e) => setOperateurId(e.target.value)} className="ds-fi" style={{ width: '100%' }}>
              <option value="">— Non assigné —</option>
              {employees.filter((e) => e.status === 'active').map((emp) => <option key={emp.id} value={emp.id}>{emp.fullName}</option>)}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Quantité *"><input type="number" min="1" value={quantite} onChange={(e) => setQuantite(e.target.value)} className="ds-fi" required style={{ width: '100%' }} placeholder="500" /></Field>
          <Field label="Date cible fin"><input type="date" value={dateCibleFin} onChange={(e) => setDateCibleFin(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
          <Field label="Temps estimé (min)"><input type="number" min="0" value={tempsEstime} onChange={(e) => setTempsEstime(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Sous-traitant"><input value={soustraitant} onChange={(e) => setSoustraitant(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="ex : Bobo Print SARL" /></Field>
          <Field label="Coût sous-traitance (FCFA)"><input type="number" min="0" value={soustraitantCout} onChange={(e) => setSoustraitantCout(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
        </div>

        <Field label="Notes techniques"><textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="ds-fi" style={{ width: '100%' }} /></Field>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!canSubmit || create.isPending || update.isPending}>{isEdit ? 'Enregistrer' : 'Créer l\'OF'}</button>
        </div>
      </form>
    </Dialog>
  );
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div><label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>{children}</div>
);
